/**
 * Primitivas de visualização da tela de insights.
 *
 * SVG puro, sem biblioteca de gráficos — o bundle já passa de 2,7 MB e as formas
 * que esta tela precisa (barra, barra empilhada, medidor) não justificam a
 * dependência.
 *
 * A paleta é a de referência validada: os cinco slots categóricos passam nas
 * verificações de banda de luminosidade, piso de croma, separação para daltonismo
 * (ΔE ≥ 8) e piso de visão normal (ΔE ≥ 15), nos dois modos, contra as
 * superfícies reais do app. Os três slots de menor contraste no modo claro
 * dependem de rótulo visível — que esta tela sempre traz, junto da tabela.
 */

import React, { useCallback, useState } from "react";

/* ------------------------------------------------------------------ paleta */

/** Ordem fixa dos slots categóricos. Nunca ciclar nem gerar cor nova. */
export const SERIES_CLARO = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"];
export const SERIES_ESCURO = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181"];

/** Cinza de deixar-em-segundo-plano, para a forma de ênfase e para "Outros". */
export const NEUTRO_CLARO = "#c3c2b7";
export const NEUTRO_ESCURO = "#52514e";

/** Status nunca é reaproveitado como série. Sempre acompanhado de rótulo. */
export const STATUS = {
  bom: "#0ca30c",
  atencao: "#fab219",
  grave: "#ec835a",
  critico: "#d03b3b",
};

export const ACENTO_CLARO = "#2a78d6";
export const ACENTO_ESCURO = "#3987e5";

/**
 * Injeta a paleta como custom properties, com os passos do modo escuro
 * escolhidos para a superfície escura — não é inversão automática.
 */
export const VizEstilos: React.FC = () => (
  <style>{`
    .viz {
      --viz-surface: #ffffff;
      --viz-ink: #0b0b0b;
      --viz-ink-2: #52514e;
      --viz-muted: #898781;
      --viz-grid: #e1e0d9;
      --viz-axis: #c3c2b7;
      --viz-accent: ${ACENTO_CLARO};
      --viz-neutral: ${NEUTRO_CLARO};
      ${SERIES_CLARO.map((c, i) => `--viz-s${i + 1}: ${c};`).join("\n      ")}
    }
    @media (prefers-color-scheme: dark) {
      :root:where(:not([data-theme="light"])) .viz {
        --viz-surface: #1d232a;
        --viz-ink: #ffffff;
        --viz-ink-2: #c3c2b7;
        --viz-muted: #898781;
        --viz-grid: #2c2c2a;
        --viz-axis: #383835;
        --viz-accent: ${ACENTO_ESCURO};
        --viz-neutral: ${NEUTRO_ESCURO};
        ${SERIES_ESCURO.map((c, i) => `--viz-s${i + 1}: ${c};`).join("\n        ")}
      }
    }
    :root[data-theme="dark"] .viz {
      --viz-surface: #1d232a;
      --viz-ink: #ffffff;
      --viz-ink-2: #c3c2b7;
      --viz-muted: #898781;
      --viz-grid: #2c2c2a;
      --viz-axis: #383835;
      --viz-accent: ${ACENTO_ESCURO};
      --viz-neutral: ${NEUTRO_ESCURO};
      ${SERIES_ESCURO.map((c, i) => `--viz-s${i + 1}: ${c};`).join("\n      ")}
    }
  `}</style>
);

/* ---------------------------------------------------------------- formato */

export const moeda = (v: number, compacto = false) =>
  compacto && Math.abs(v) >= 1000
    ? `R$ ${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`
    : v.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0,
      });

export const percentual = (v: number, casas = 0) =>
  `${(v * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}%`;

export const horas = (h: number) =>
  h < 1
    ? `${Math.round(h * 60)} min`
    : h < 48
      ? `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`
      : `${Math.round(h / 24)} dias`;

/* --------------------------------------------------------------- geometria */

/**
 * Barra horizontal com a ponta do dado arredondada em 4px e a base reta —
 * a barra cresce de uma linha de base única, e o raio marca onde o dado termina.
 */
export function caminhoBarra(x: number, y: number, largura: number, altura: number) {
  const r = Math.min(4, Math.max(0, largura));
  if (largura <= r) return `M${x},${y}h${largura}v${altura}h${-largura}z`;
  return [
    `M${x},${y}`,
    `h${largura - r}`,
    `a${r},${r} 0 0 1 ${r},${r}`,
    `v${altura - 2 * r}`,
    `a${r},${r} 0 0 1 ${-r},${r}`,
    `h${-(largura - r)}`,
    "z",
  ].join("");
}

/* ------------------------------------------------------------------ blocos */

export const Cartao: React.FC<{
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
  acao?: React.ReactNode;
}> = ({ titulo, descricao, children, acao }) => (
  <section className="viz rounded-xl border border-base-300 bg-base-100 p-5">
    <header className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold text-base-content">{titulo}</h2>
        {descricao && (
          <p className="mt-0.5 text-sm text-base-content/60">{descricao}</p>
        )}
      </div>
      {acao}
    </header>
    {children}
  </section>
);

/** Número que a tela lidera. Exatamente um por visão, ≥48px, mesma fonte sans. */
export const NumeroHeroi: React.FC<{
  rotulo: string;
  valor: string;
  apoio?: React.ReactNode;
}> = ({ rotulo, valor, apoio }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
      {rotulo}
    </p>
    <p className="mt-1 text-5xl font-semibold leading-none text-base-content">
      {valor}
    </p>
    {apoio && <div className="mt-2 text-sm text-base-content/70">{apoio}</div>}
  </div>
);

export const Indicador: React.FC<{
  rotulo: string;
  valor: string;
  apoio?: string;
  tom?: "neutro" | "bom" | "atencao" | "critico";
}> = ({ rotulo, valor, apoio, tom = "neutro" }) => {
  const cor =
    tom === "bom"
      ? STATUS.bom
      : tom === "atencao"
        ? STATUS.atencao
        : tom === "critico"
          ? STATUS.critico
          : undefined;

  return (
    <div className="rounded-xl border border-base-300 bg-base-100 p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-base-content/60">
        {rotulo}
      </p>
      <p className="mt-1 flex items-center gap-2 text-2xl font-semibold text-base-content">
        {cor && (
          // Ponto colorido ao lado do número: a identidade fica na marca, nunca
          // no texto, que sempre usa tinta de texto.
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: cor }}
          />
        )}
        {valor}
      </p>
      {apoio && <p className="mt-1 text-xs text-base-content/60">{apoio}</p>}
    </div>
  );
};

/** Medidor: uma razão contra um limite. Trilha é passo claro do mesmo tom. */
export const Medidor: React.FC<{
  fracao: number;
  rotulo: string;
  detalhe?: string;
  tom: "bom" | "atencao" | "critico";
}> = ({ fracao, rotulo, detalhe, tom }) => {
  const cor = tom === "bom" ? STATUS.bom : tom === "atencao" ? STATUS.atencao : STATUS.critico;
  const pct = Math.max(0, Math.min(1, fracao));

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-sm text-base-content/70">{rotulo}</span>
        <span className="text-2xl font-semibold text-base-content">
          {percentual(pct)}
        </span>
      </div>
      <div
        className="h-3 w-full overflow-hidden rounded-full"
        style={{ background: `${cor}26` }}
        role="meter"
        aria-valuenow={Math.round(pct * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={rotulo}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct * 100}%`, background: cor }}
        />
      </div>
      {detalhe && <p className="mt-2 text-xs text-base-content/60">{detalhe}</p>}
    </div>
  );
};

interface PosicaoDica<T> {
  x: number;
  y: number;
  dado: T;
}

/**
 * Caixa do tooltip. Componente de módulo, não criado dentro do hook: um
 * componente redefinido a cada render seria um tipo novo para o React, que
 * desmontaria e remontaria a caixa em cada movimento do mouse.
 */
function CaixaDica<T>({
  dica,
  children,
}: {
  dica: PosicaoDica<T> | null;
  children: (dado: T) => React.ReactNode;
}) {
  if (!dica) return null;
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-10 max-w-xs rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-xs shadow-lg"
      style={{ left: Math.max(0, dica.x + 12), top: Math.max(0, dica.y - 8) }}
    >
      {children(dica.dado)}
    </div>
  );
}

/** Tooltip de gráfico. Segue o ponteiro dentro do container relativo. */
export function useDica<T>() {
  const [dica, setDica] = useState<PosicaoDica<T> | null>(null);

  const mostrar = useCallback((e: React.MouseEvent | React.FocusEvent, dado: T) => {
    const alvo = e.currentTarget as unknown as SVGElement;
    const svg = alvo.ownerSVGElement ?? (alvo as unknown as SVGSVGElement);
    const box = svg?.getBoundingClientRect();
    if (!box) return;

    // Evento de foco (navegação por teclado) não traz coordenada de ponteiro:
    // ancora no próprio elemento em vez de no cursor.
    const ponteiro = "clientX" in e ? e : null;
    const alvoBox = alvo.getBoundingClientRect();

    setDica({
      x: (ponteiro ? ponteiro.clientX : alvoBox.left) - box.left,
      y: (ponteiro ? ponteiro.clientY : alvoBox.top) - box.top,
      dado,
    });
  }, []);

  const esconder = useCallback(() => setDica(null), []);

  const Dica = useCallback(
    ({ children }: { children: (dado: T) => React.ReactNode }) => (
      <CaixaDica dica={dica} children={children} />
    ),
    [dica],
  );

  return { mostrar, esconder, Dica };
}

/** Aviso de dado ausente, no lugar de um gráfico vazio sem explicação. */
export const SemDados: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="rounded-lg border border-dashed border-base-300 px-4 py-8 text-center text-sm text-base-content/60">
    {children}
  </p>
);
